/**
 * The v2 composite response, in the shapes the KPI section already renders
 * (US-22).
 *
 * The cutover is a change of *source*, not of presentation, and US-22's
 * hardest criterion is that the rendered figures do not move. Adapting here —
 * in one pure function with no React and no network — is what makes that
 * criterion testable: the old shapes and the new ones can be compared
 * directly, which a component that fetched its own data never allowed.
 *
 * Nothing is computed on the way through. Every value is carried across as
 * the server sent it; a second implementation of the KPI arithmetic is
 * precisely how v1 and v2 would start to disagree.
 */

import type {
  CompositeResponse,
  DashboardMetricKey,
  SignalName,
} from '@delivery-metrics/shared';

import type {
  LeadershipKPIOverview,
  MetricDataPoint,
  PredictabilityDataPoint,
} from '../services/python-api';

/**
 * The two vocabularies for the same five measures.
 *
 * The frontend speaks `snake_case` signal names, the v2 contract speaks
 * `camelCase` metric keys. Declared once rather than converted with a string
 * transform, so a rename on either side is a type error here instead of a
 * silently empty chart.
 */
export const METRIC_KEY_BY_SIGNAL: Record<SignalName, DashboardMetricKey> = {
  cycle_time: 'cycleTime',
  delivery_velocity: 'deliveryVelocity',
  test_coverage: 'testCoverage',
  defect_escape_rate: 'defectEscapeRate',
  on_time_predictability: 'onTimePredictability',
};

const SIGNALS = Object.keys(METRIC_KEY_BY_SIGNAL) as SignalName[];

/**
 * The card values, units, descriptions and fitted trend lines.
 *
 * The four `*_pct` fields keep their v1 names because the section reads them
 * by those names; renaming them would be presentation churn inside a story
 * that promises none.
 */
export function compositeToLeadershipOverview(
  composite: CompositeResponse
): LeadershipKPIOverview {
  return {
    cycle_time_reduction_pct: composite.cycleTime.summary.current,
    delivery_velocity_increase_pct: composite.deliveryVelocity.summary.current,
    test_coverage_increase_pct: composite.testCoverage.summary.current,
    defect_escape_rate_reduction_pct:
      composite.defectEscapeRate.summary.current,
    on_time_predictability_improvement_pct:
      composite.onTimePredictability.summary.current,
    summaryUnits: {
      cycle_time: composite.cycleTime.summary.unit,
      delivery_velocity: composite.deliveryVelocity.summary.unit,
      test_coverage: composite.testCoverage.summary.unit,
      defect_escape_rate: composite.defectEscapeRate.summary.unit,
      on_time_predictability: composite.onTimePredictability.summary.unit,
    },
    trendLines: Object.fromEntries(
      SIGNALS.map(signal => [
        signal,
        composite[METRIC_KEY_BY_SIGNAL[signal]].trendLine,
      ])
    ),
  };
}

/** The chart series per signal, keyed the way the section indexes them. */
export function compositeToMetricSeries(
  composite: CompositeResponse
): Record<SignalName, MetricDataPoint[]> {
  return Object.fromEntries(
    SIGNALS.map(signal => [
      signal,
      composite[METRIC_KEY_BY_SIGNAL[signal]].series.map(point => ({
        timestamp: point.timestamp,
        label: point.label,
        value: point.value,
      })),
    ])
  ) as Record<SignalName, MetricDataPoint[]>;
}

/**
 * The two predictability readings per bucket, in the shape the KPI-5 card
 * reads. The block's `value` is the PI reading; each level also rides in
 * `components`, where it is **absent** (not zero) for a bucket with no
 * snapshot — which is why this maps absence to `null` rather than defaulting.
 */
export function compositeToPredictabilityPoints(
  composite: CompositeResponse
): PredictabilityDataPoint[] {
  return composite.onTimePredictability.series.map(point => ({
    timestamp: point.timestamp,
    label: point.label,
    pi_predictability_pct: point.components.piPredictabilityPct ?? null,
    release_predictability_pct:
      point.components.releasePredictabilityPct ?? null,
    pi_names: point.piNames ?? [],
    release_names: point.releaseNames ?? [],
  }));
}

/** The sentence the server published for a metric, for the card subtitle. */
export function compositeDescription(
  composite: CompositeResponse,
  signal: SignalName
): string {
  return composite[METRIC_KEY_BY_SIGNAL[signal]].summary.description;
}
